import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getStats, listCampaigns } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Users,
  ArrowRight,
} from "@phosphor-icons/react";

function Metric({ label, value, suffix, icon: Icon, accent }) {
  return (
    <div className="border border-[#E5E7EB] p-6 bg-white -ml-px -mt-px animate-fade-up">
      <div className="flex items-center justify-between mb-6">
        <span className="text-[11px] tracking-[0.2em] uppercase text-[#52525B] font-semibold">
          {label}
        </span>
        <Icon size={20} weight="bold" color={accent || "#A1A1AA"} />
      </div>
      <div className="font-display font-extrabold text-5xl tracking-tight text-[#09090B]">
        {value}
        {suffix && <span className="text-2xl text-[#A1A1AA] ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
    listCampaigns().then((c) => setCampaigns(c.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        subtitle="Send emails with a custom From-name, a separate Reply-To, and deliverability-friendly headers."
        action={
          <button
            data-testid="dashboard-compose-btn"
            onClick={() => navigate("/compose")}
            className="rounded-none bg-[#09090B] text-white px-6 py-3 text-sm font-semibold flex items-center gap-2 hover:bg-[#2563EB] transition-colors duration-150"
          >
            Compose email <ArrowRight size={16} weight="bold" />
          </button>
        }
      />

      <div className="p-10">
        <div className="grid grid-cols-1 md:grid-cols-4 border-t border-l border-[#E5E7EB]">
          <Metric label="Emails Sent" value={stats?.total_sent ?? 0} icon={CheckCircle} accent="#10B981" />
          <Metric label="Failed" value={stats?.total_failed ?? 0} icon={XCircle} accent="#EF4444" />
          <Metric label="Delivery Rate" value={stats?.delivery_rate ?? 0} suffix="%" icon={PaperPlaneTilt} accent="#2563EB" />
          <Metric label="Campaigns" value={stats?.total_campaigns ?? 0} icon={Users} accent="#09090B" />
        </div>

        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl tracking-tight">Recent sends</h2>
            <button
              onClick={() => navigate("/history")}
              className="text-sm text-[#2563EB] font-semibold hover:underline"
            >
              View all
            </button>
          </div>

          {campaigns.length === 0 ? (
            <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-16 text-center">
              <p className="text-[#52525B] mb-4">No emails sent yet.</p>
              <button
                data-testid="empty-compose-btn"
                onClick={() => navigate("/compose")}
                className="rounded-none bg-[#09090B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#2563EB] transition-colors duration-150"
              >
                Send your first email
              </button>
            </div>
          ) : (
            <div className="border border-[#E5E7EB] bg-white">
              <table className="w-full text-sm" data-testid="recent-campaigns-table">
                <thead>
                  <tr className="text-left text-[11px] tracking-[0.15em] uppercase text-[#A1A1AA] border-b border-[#E5E7EB]">
                    <th className="px-4 py-3 font-semibold">Subject</th>
                    <th className="px-4 py-3 font-semibold">From</th>
                    <th className="px-4 py-3 font-semibold">Recipients</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => navigate("/history")}
                      className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors duration-150"
                    >
                      <td className="px-4 py-3 font-medium max-w-xs truncate">{c.subject}</td>
                      <td className="px-4 py-3 text-[#52525B]">{c.from_name}</td>
                      <td className="px-4 py-3 font-mono">{c.total}</td>
                      <td className="px-4 py-3">
                        <span className="text-[#10B981] font-medium">{c.sent_count} sent</span>
                        {c.failed_count > 0 && (
                          <span className="text-[#EF4444] ml-2">{c.failed_count} failed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#A1A1AA] font-mono text-xs">
                        {new Date(c.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
